import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CreditCard,
  FileText,
  DollarSign,
  Calendar,
  TrendingDown,
  Download,
  Upload,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { loansApi, paymentsApi } from '../../api';
import { API_URL } from '../../api/client';
import { Loan, PaymentSchedule, Payment, PaymentMethod } from '../../types';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { StatusBadge } from '../../components/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/Button';
import { CurrencyInput } from '../../components/CurrencyInput';
import { formatCurrency } from '../../utils/currency';

export const LoanDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loan, setLoan] = useState<Loan | null>(null);
  const [schedule, setSchedule] = useState<PaymentSchedule[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: PaymentMethod.BANK_TRANSFER,
    transaction_reference: '',
    notes: '',
    bank_name: '',
    payment_date: new Date().toISOString().split('T')[0], // Default to today
  });
  const [paymentProof, setPaymentProof] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [initializingGatewayPayment, setInitializingGatewayPayment] = useState(false);

  const [earlySettlement, setEarlySettlement] = useState<any>(null);
  const [showEarlySettlement, setShowEarlySettlement] = useState(false);
  const [loadingSettlement, setLoadingSettlement] = useState(false);

  useEffect(() => {
    if (!id) {
      navigate('/customer/loans');
      return;
    }

    fetchLoanData();
  }, [id]);

  const fetchLoanData = async () => {
    try {
      const [loanResponse, scheduleResponse, paymentsResponse] = await Promise.all([
        loansApi.getById(id!),
        loansApi.getPaymentSchedule(id!),
        loansApi.getPayments(id!),
      ]);

      if (loanResponse.success && loanResponse.data) {
        setLoan(loanResponse.data.loan);
      }
      if (scheduleResponse.success && scheduleResponse.data) {
        setSchedule(scheduleResponse.data.schedule);
      }
      if (paymentsResponse.success && paymentsResponse.data) {
        setPayments(paymentsResponse.data.payments);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load loan details');
    } finally {
      setLoading(false);
    }
  };

  const fetchEarlySettlement = async () => {
    setLoadingSettlement(true);
    try {
      const response = await loansApi.calculateEarlySettlement(id!);
      if (response.success && response.data) {
        setEarlySettlement(response.data.settlement);
        setShowEarlySettlement(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to calculate early settlement');
    } finally {
      setLoadingSettlement(false);
    }
  };

  const handleGatewayPayment = async () => {
    setInitializingGatewayPayment(true);
    setError('');

    try {
      if (!user?.email) {
        setError('User email not found. Please log in again.');
        return;
      }

      const totalOutstanding = parseFloat(loan!.outstanding_principal) + parseFloat(loan!.outstanding_interest);

      const response = await paymentsApi.initialize({
        loan_id: id!,
        amount: totalOutstanding,
        email: user.email,
      });

      if (response.success && response.data) {
        // Redirect to gateway authorization URL
        if (response.data.authorization_url) {
          window.location.href = response.data.authorization_url;
        } else {
          setError('Payment gateway did not return authorization URL');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to initialize payment');
    } finally {
      setInitializingGatewayPayment(false);
    }
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await paymentsApi.record({
        loan_id: id!,
        amount: parseFloat(paymentData.amount),
        payment_method: paymentData.payment_method,
        transaction_reference: paymentData.transaction_reference,
        notes: paymentData.notes,
        bank_name: paymentData.bank_name,
        payment_date: paymentData.payment_date,
        payment_proof: paymentProof || undefined,
      });

      if (response.success) {
        setSuccessMessage('Payment submitted successfully and is pending verification');
        setShowPaymentForm(false);
        setPaymentData({
          amount: '',
          payment_method: PaymentMethod.BANK_TRANSFER,
          transaction_reference: '',
          notes: '',
          bank_name: '',
          payment_date: new Date().toISOString().split('T')[0],
        });
        setPaymentProof(null);
        fetchLoanData();
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!loan) {
    return (
      <div className="error-state">
        <p>Loan not found</p>
      </div>
    );
  }

  return (
    <div className="loan-details-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Loan Details</h1>
          <p>View your loan information, make payments, and track your repayment schedule</p>
        </div>
        <Button onClick={() => navigate('/customer/loans')} variant="secondary" icon={<ArrowLeft size={20} />}>
          Back to Loans
        </Button>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {successMessage && (
        <div className="success-message">
          <span>{successMessage}</span>
        </div>
      )}

      <div className="loan-info-card">
        <div className="card-header">
          <h2>{loan.loan_reference}</h2>
          <StatusBadge status={loan.is_overdue ? 'overdue' : loan.status} />
        </div>
        <div className="loan-info-grid">
          <div className="info-item">
            <div className="info-icon">
              <FileText size={18} />
            </div>
            <div>
              <span className="label">Product</span>
              <span className="value">{loan.product_name || 'N/A'}</span>
            </div>
          </div>
          <div className="info-item">
            <div className="info-icon">
              <DollarSign size={18} />
            </div>
            <div>
              <span className="label">Principal Amount</span>
              <span className="value">
                {formatCurrency(loan.principal_amount)}
              </span>
            </div>
          </div>
          <div className="info-item">
            <div className="info-icon">
              <TrendingDown size={18} />
            </div>
            <div>
              <span className="label">Interest Rate</span>
              <span className="value">{parseFloat(loan.interest_rate).toFixed(2)}%</span>
            </div>
          </div>
          <div className="info-item">
            <div className="info-icon">
              <Calendar size={18} />
            </div>
            <div>
              <span className="label">Loan Term</span>
              <span className="value">{loan.loan_term_months} months</span>
            </div>
          </div>
          <div className="info-item">
            <div className="info-icon">
              <CreditCard size={18} />
            </div>
            <div>
              <span className="label">Monthly Payment</span>
              <span className="value">
                {formatCurrency(loan.monthly_payment)}
              </span>
            </div>
          </div>
          <div className="info-item">
            <div className="info-icon">
              <AlertCircle size={18} />
            </div>
            <div>
              <span className="label">Outstanding Principal</span>
              <span className="value">
                {formatCurrency(loan.outstanding_principal)}
              </span>
            </div>
          </div>
          <div className="info-item">
            <div className="info-icon">
              <AlertCircle size={18} />
            </div>
            <div>
              <span className="label">Outstanding Interest</span>
              <span className="value">
                {formatCurrency(loan.outstanding_interest)}
              </span>
            </div>
          </div>
          <div className="info-item">
            <div className="info-icon">
              <CheckCircle size={18} />
            </div>
            <div>
              <span className="label">Total Paid</span>
              <span className="value">
                {formatCurrency(loan.total_paid)}
              </span>
            </div>
          </div>
          <div className="info-item">
            <div className="info-icon">
              <Calendar size={18} />
            </div>
            <div>
              <span className="label">Disbursement Date</span>
              <span className="value">{new Date(loan.disbursement_date).toLocaleDateString()}</span>
            </div>
          </div>
          <div className="info-item">
            <div className="info-icon">
              <Calendar size={18} />
            </div>
            <div>
              <span className="label">Maturity Date</span>
              <span className="value">{new Date(loan.maturity_date).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        {!showPaymentForm && (
          <div className="payment-actions">
            <Button
              onClick={handleGatewayPayment}
              variant="primary"
              icon={<CreditCard size={20} />}
              loading={initializingGatewayPayment}
              disabled={initializingGatewayPayment}
            >
              Pay Online Now
            </Button>
            <Button onClick={() => setShowPaymentForm(true)} variant="secondary" icon={<Upload size={20} />}>
              Submit Manual Payment
            </Button>
            <Button
              onClick={fetchEarlySettlement}
              variant="secondary"
              icon={<TrendingDown size={20} />}
              loading={loadingSettlement}
              disabled={loadingSettlement}
            >
              Calculate Early Payoff
            </Button>
          </div>
        )}

        <div className="payment-note">
          <p>
            <strong>Note:</strong> Partial payments are accepted. Any amount you pay will be applied
            to your loan balance and payment schedule.
          </p>
        </div>
      </div>

      {showEarlySettlement && earlySettlement && (
        <div className="early-settlement-card">
          <div className="card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TrendingDown size={24} />
              <h3>Early Settlement Quote</h3>
            </div>
            <Button
              onClick={() => setShowEarlySettlement(false)}
              variant="secondary"
              size="sm"
            >
              Close
            </Button>
          </div>

          <div className="settlement-info">
            <div className="info-highlight">
              <div className="highlight-item">
                <span className="label">Total Payoff Amount</span>
                <span className="value amount-large">
                  {formatCurrency(earlySettlement.total_payoff_amount)}
                </span>
              </div>
              <div className="highlight-item success">
                <span className="label">Your Savings</span>
                <span className="value amount-large">
                  {formatCurrency(earlySettlement.total_savings)}
                </span>
              </div>
            </div>

            <div className="settlement-breakdown">
              <h4>Breakdown</h4>
              <div className="breakdown-grid">
                <div className="breakdown-item">
                  <span className="label">Outstanding Principal:</span>
                  <span className="value">{formatCurrency(earlySettlement.outstanding_principal)}</span>
                </div>
                <div className="breakdown-item">
                  <span className="label">Prorated Interest:</span>
                  <span className="value">{formatCurrency(earlySettlement.prorated_interest)}</span>
                </div>
                {earlySettlement.early_settlement_fee > 0 && (
                  <div className="breakdown-item">
                    <span className="label">Early Settlement Fee ({earlySettlement.early_settlement_fee_percentage}%):</span>
                    <span className="value">{formatCurrency(earlySettlement.early_settlement_fee)}</span>
                  </div>
                )}
                <div className="breakdown-item">
                  <span className="label">Future Interest Waived:</span>
                  <span className="value success">-{formatCurrency(earlySettlement.interest_savings)}</span>
                </div>
              </div>
            </div>

            <div className="settlement-details">
              <h4>Loan Timeline</h4>
              <div className="timeline-info">
                <div className="timeline-item">
                  <span className="label">Time Elapsed:</span>
                  <span className="value">{earlySettlement.months_elapsed} months</span>
                </div>
                <div className="timeline-item">
                  <span className="label">Time Remaining:</span>
                  <span className="value">{earlySettlement.months_remaining} months</span>
                </div>
                <div className="timeline-item">
                  <span className="label">Original Maturity:</span>
                  <span className="value">{new Date(earlySettlement.maturity_date).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            <div className="settlement-action">
              <p className="info-text">
                Pay off your loan early and save {formatCurrency(earlySettlement.total_savings)} in interest!
              </p>
              <Button
                onClick={() => {
                  setPaymentData({ ...paymentData, amount: earlySettlement.total_payoff_amount.toString() });
                  setShowPaymentForm(true);
                  setShowEarlySettlement(false);
                }}
                variant="primary"
                size="lg"
              >
                Proceed to Pay Off Loan
              </Button>
            </div>
          </div>
        </div>
      )}

      {showPaymentForm && (
        <div className="payment-form-card">
          <h3>Submit Payment Details</h3>
          <p className="form-description">
            Please fill in your payment details below. Your payment will be verified by our team before being processed.
          </p>
          <form onSubmit={handlePaymentSubmit}>
            <div className="form-row">
              <div className="form-group">
                <CurrencyInput
                  id="payment-amount"
                  name="amount"
                  label="Payment Amount"
                  value={paymentData.amount}
                  onChange={(value) => {
                    setPaymentData({ ...paymentData, amount: value.toString() });
                  }}
                  required
                  min={0}
                  placeholder="Enter payment amount"
                />
              </div>

              <div className="form-group">
                <label htmlFor="payment_date">Payment Date *</label>
                <input
                  type="date"
                  id="payment_date"
                  value={paymentData.payment_date}
                  onChange={(e) =>
                    setPaymentData({ ...paymentData, payment_date: e.target.value })
                  }
                  required
                  disabled={submitting}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="payment_method">Payment Method *</label>
                <select
                  id="payment_method"
                  value={paymentData.payment_method}
                  onChange={(e) =>
                    setPaymentData({
                      ...paymentData,
                      payment_method: e.target.value as PaymentMethod,
                    })
                  }
                  required
                  disabled={submitting}
                >
                  <option value={PaymentMethod.BANK_TRANSFER}>Bank Transfer</option>
                  <option value={PaymentMethod.CARD}>Card</option>
                  <option value={PaymentMethod.USSD}>USSD</option>
                  <option value={PaymentMethod.MOBILE_MONEY}>Mobile Money</option>
                  <option value={PaymentMethod.CASH}>Cash</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="bank_name">Bank Name *</label>
                <input
                  type="text"
                  id="bank_name"
                  value={paymentData.bank_name}
                  onChange={(e) =>
                    setPaymentData({ ...paymentData, bank_name: e.target.value })
                  }
                  required
                  disabled={submitting}
                  placeholder="e.g., First Bank, GTBank, Access Bank"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="transaction_reference">Transaction Reference *</label>
              <input
                type="text"
                id="transaction_reference"
                value={paymentData.transaction_reference}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, transaction_reference: e.target.value })
                }
                required
                disabled={submitting}
                placeholder="Enter transaction reference from bank"
              />
            </div>

            <div className="form-group">
              <label htmlFor="payment_proof">Payment Proof (Receipt/Screenshot) *</label>
              <input
                type="file"
                id="payment_proof"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    // Validate file size (5MB max)
                    if (file.size > 5 * 1024 * 1024) {
                      setError('File size must be less than 5MB');
                      e.target.value = '';
                      return;
                    }
                    // Validate file type
                    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
                    if (!allowedTypes.includes(file.type)) {
                      setError('Only JPG, PNG, and PDF files are allowed');
                      e.target.value = '';
                      return;
                    }
                    setPaymentProof(file);
                    setError('');
                  }
                }}
                required
                disabled={submitting}
                accept=".jpg,.jpeg,.png,.pdf"
              />
              <small className="form-hint">
                Upload a clear image or PDF of your payment receipt (Max 5MB)
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="notes">Additional Notes</label>
              <textarea
                id="notes"
                value={paymentData.notes}
                onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                disabled={submitting}
                rows={3}
                placeholder="Any additional information about this payment"
              />
            </div>

            <div className="form-actions">
              <Button
                type="button"
                onClick={() => {
                  setShowPaymentForm(false);
                  setPaymentProof(null);
                }}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={submitting} disabled={submitting}>
                Submit for Verification
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="payment-schedule-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Calendar size={24} />
          <h3>Payment Schedule</h3>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Installment</th>
                <th>Due Date</th>
                <th>Principal</th>
                <th>Interest</th>
                <th>Total</th>
                <th>Amount Paid</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((item) => (
                <tr key={item.schedule_id}>
                  <td>{item.installment_number}</td>
                  <td>{new Date(item.due_date).toLocaleDateString()}</td>
                  <td>
                    {formatCurrency(item.principal_amount)}
                  </td>
                  <td>
                    {formatCurrency(item.interest_amount)}
                  </td>
                  <td>
                    {formatCurrency(item.total_amount)}
                  </td>
                  <td>
                    {formatCurrency(item.amount_paid)}
                  </td>
                  <td>
                    <StatusBadge
                      status={item.is_paid ? 'paid' : item.is_overdue ? 'overdue' : 'pending'}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {payments.length > 0 && (
        <div className="payments-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <CheckCircle size={24} />
            <h3>Payment History</h3>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.payment_id}>
                    <td className="font-mono">{payment.payment_reference}</td>
                    <td>{new Date(payment.payment_date).toLocaleDateString()}</td>
                    <td className="font-semibold">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td>{payment.payment_method}</td>
                    <td>
                      <StatusBadge status={payment.status} />
                    </td>
                    <td>
                      {payment.status === 'successful' && (
                        <Button
                          onClick={() => window.open(`${API_URL}/payments/${payment.payment_id}/receipt`, '_blank')}
                          variant="secondary"
                          size="sm"
                          icon={<Download size={16} />}
                        >
                          Download Receipt
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <style>{`
        /* Tablet Responsive Styles */
        @media (max-width: 1024px) {
          .page-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }

          .page-header button {
            width: 100%;
          }

          .loan-info-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .payment-actions {
            flex-direction: column;
            gap: 0.75rem;
          }

          .payment-actions button {
            width: 100%;
          }

          .settlement-breakdown {
            grid-template-columns: 1fr;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .table-container {
            overflow-x: auto;
          }

          .data-table {
            min-width: 800px;
          }
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .loan-details-page {
            padding: 1rem;
          }

          .page-header h1 {
            font-size: 1.75rem;
          }

          .page-header p {
            font-size: 0.9375rem;
          }

          .loan-info-card {
            padding: 1.25rem;
          }

          .card-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }

          .card-header h2 {
            font-size: 1.25rem;
          }

          .loan-info-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .info-item {
            padding: 0.875rem;
          }

          .payment-actions {
            flex-direction: column;
            gap: 0.75rem;
          }

          .payment-actions button {
            width: 100%;
          }

          .payment-note {
            padding: 1rem;
            font-size: 0.875rem;
          }

          .early-settlement-card {
            padding: 1.25rem;
          }

          .settlement-info {
            gap: 1.25rem;
          }

          .info-highlight {
            flex-direction: column;
            gap: 1rem;
          }

          .highlight-item {
            width: 100%;
          }

          .settlement-breakdown {
            grid-template-columns: 1fr;
          }

          .settlement-action button {
            width: 100%;
          }

          .payment-form-card {
            padding: 1.25rem;
          }

          .payment-form-card h3 {
            font-size: 1.25rem;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .form-group label {
            font-size: 0.875rem;
          }

          .form-group input,
          .form-group select,
          .form-group textarea {
            padding: 0.875rem;
            font-size: 1rem;
          }

          .form-actions {
            flex-direction: column;
            gap: 0.75rem;
          }

          .form-actions button {
            width: 100%;
          }

          .table-container {
            border-radius: 0.75rem;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .data-table {
            min-width: 900px;
            font-size: 0.875rem;
          }

          .data-table th,
          .data-table td {
            padding: 0.75rem 0.5rem;
          }

          .data-table th:first-child,
          .data-table td:first-child {
            position: sticky;
            left: 0;
            background-color: white;
            z-index: 1;
          }

          .data-table th:first-child {
            background-color: var(--color-neutral-50);
          }
        }

        /* Small Mobile Responsive Styles */
        @media (max-width: 480px) {
          .loan-details-page {
            padding: 0.875rem;
          }

          .page-header h1 {
            font-size: 1.5rem;
          }

          .loan-info-card {
            padding: 1rem;
          }

          .card-header h2 {
            font-size: 1.125rem;
          }

          .info-item {
            padding: 0.75rem;
          }

          .info-icon {
            width: 2rem;
            height: 2rem;
          }

          .info-icon svg {
            width: 16px;
            height: 16px;
          }

          .label {
            font-size: 0.8125rem;
          }

          .value {
            font-size: 0.9375rem;
          }

          .payment-actions button {
            padding: 0.75rem 1rem;
            font-size: 0.9375rem;
          }

          .early-settlement-card {
            padding: 1rem;
          }

          .amount-large {
            font-size: 1.5rem;
          }

          .payment-form-card {
            padding: 1rem;
          }

          .form-group input,
          .form-group select,
          .form-group textarea {
            padding: 0.75rem;
            font-size: 0.9375rem;
          }

          .data-table {
            font-size: 0.8125rem;
          }
        }
      `}</style>
    </div>
  );
};
